import { createSelector } from '@reduxjs/toolkit';

import {
  CategoryName,
  DbId,
  IPrivsTreeAction,
  TAffiliationSign,
  TPaneSign,
  TRoleId,
  TTreeId,
} from '../../models/role-privs/RolePrivsData';
import { AppState } from '../reducers/root';
import {
  IPrivsCategoryTreeStateEntity,
  IPrivsMainTreeStateEntity,
  RolePrivsState,
  TPrivsTreeState,
} from '../slices/rolePrivs';

const getPrivsTreesState = (state: AppState): TPrivsTreeState =>
  state.rolePrivs.trees;

export const getPrivsState = (state: AppState): RolePrivsState =>
  state.rolePrivs;

export const getPrivsTreeState = createSelector(
  [getPrivsTreesState, (_state, treeId: TTreeId) => treeId],
  (treesState, treeId) => treesState[treeId]
);

export const getPrivsCategoryName = (state: AppState): CategoryName =>
  state.rolePrivs.categoryName;

export const getPrivsRoleId = (state: AppState): TRoleId =>
  state.rolePrivs.roleId;

export const getPrivsInherit = (state: AppState): boolean =>
  state.rolePrivs.inherit;

export const getPrivsTreeActiveId = createSelector(
  getPrivsTreeState,
  treeState => treeState?.activeId
);

export const getPrivsCategoryTreeActiveCategoryName = createSelector(
  [getPrivsTreeState, getPrivsTreeActiveId],
  (treeState, activeId) =>
    treeState?.nodes.find(node => node.id === activeId)?.name
);

export const getTreePane = createSelector(
  getPrivsTreeState,
  treeState => treeState.pane
);

export const getPrivsTreeActions = (
  state: AppState,
  treeId: TTreeId
): IPrivsTreeAction[] | undefined =>
  state.rolePrivs.treeActions[treeId]?.actions;

const getPrivsTreeActionsStateDisabled = (
  state: AppState,
  treeId: TTreeId
): boolean => state.rolePrivs.treeActions[treeId]?.disabled ?? true;

export const getIsCategoryTree = createSelector(
  [getPrivsTreeState],
  treeState => treeState?.isCategoryTree ?? false
);

const findCategoryTrees = createSelector([getPrivsTreesState], treesState =>
  Object.values(treesState).filter(
    (tree): tree is IPrivsCategoryTreeStateEntity => tree.isCategoryTree
  )
);

const findMainTrees = createSelector([getPrivsTreesState], treesState =>
  Object.values(treesState).filter(
    (tree): tree is IPrivsMainTreeStateEntity => !tree.isCategoryTree
  )
);

export const findCategoryTreeByPane = createSelector(
  [findCategoryTrees, (_state, pane: TPaneSign) => pane],
  (categoryTrees, pane) => categoryTrees.filter(tree => tree.pane === pane)[0]
);

export const findMainTreeByPane = createSelector(
  [findMainTrees, (_state, pane: TAffiliationSign) => pane],
  (mainTrees, pane) => mainTrees.filter(tree => tree.pane === pane)[0] ?? {}
);

export const findCategoryTreeById = createSelector(
  [findCategoryTrees, (_state, treeId: TTreeId) => treeId],
  (categoryTrees, treeId) =>
    categoryTrees.filter(tree => tree.treeId === treeId)[0]
);

export const findMainTreeById = createSelector(
  [findMainTrees, (_state, treeId: TTreeId) => treeId],
  (mainTrees, treeId) => mainTrees.filter(tree => tree.treeId === treeId)[0]
);

export const getTreeNodesChecked = createSelector(
  [findMainTreeById, (_state, _treeId, nodeId: DbId) => nodeId],
  (mainTree, nodeId) => mainTree.checkedNodes.includes(nodeId)
);

const getMainTreeActiveNodeChecked = createSelector(
  [findMainTreeByPane],
  mainTree => {
    const { activeId, checkedNodes } = mainTree;
    return !!activeId && checkedNodes.includes(activeId);
  }
);

export const getPrivsTreeActionsDisabled = (
  state: AppState,
  treeId: TTreeId,
  pane: TAffiliationSign
): boolean =>
  !getMainTreeActiveNodeChecked(state, pane) ||
  getPrivsTreeActionsStateDisabled(state, treeId);
